import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { billingApi } from '../services/billingApi';
import type { ProviderSummary } from '../types/billing';
import ProvidersTable from './providers/ProvidersTable';
import ProvidersByBiller from './providers/ProvidersByBiller';

type TabKey = 'all' | 'byBiller';

const Providers: React.FC = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<ProviderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('all');

  useEffect(() => {
    let cancelled = false;
    billingApi
      .listProviders()
      .then((rows) => {
        if (!cancelled) setProviders(rows);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Failed to load providers');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const goToPractice = (provider: ProviderSummary) =>
    navigate(`/practices/${provider.practiceId}`);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Providers
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Physicians, mid-levels, and other practitioners being credentialed. Click any provider to open their practice.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
        >
          <Tab value="all" label="All providers" />
          <Tab value="byBiller" label="By biller" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : tab === 'all' ? (
          <ProvidersTable providers={providers} onRowClick={goToPractice} />
        ) : (
          <ProvidersByBiller providers={providers} onProviderClick={goToPractice} />
        )}
      </Paper>
    </Box>
  );
};

export default Providers;
